import { useRef } from "react";

const SWIPE_THRESHOLD = 30;
export const SWIPE_GUARD_CLASS = "swipe-guard";

function hasSwipeGuard(el: Element | null): boolean {
    while (el && el !== document.body) {
        if (el.classList.contains(SWIPE_GUARD_CLASS)) return true;
        el = el.parentElement;
    }
    return false;
}

function hasHorizontalScroll(el: Element | null): boolean {
    while (el && el !== document.body) {
        if (el.classList.contains(SWIPE_GUARD_CLASS)) return true;
        const style = getComputedStyle(el);
        if (
            el.scrollWidth > el.clientWidth &&
            (style.overflowX === "auto" || style.overflowX === "scroll")
        ) return true;
        el = el.parentElement;
    }
    return false;
}

function hasVerticalScroll(el: Element | null): boolean {
    while (el && el !== document.body) {
        if (el.classList.contains(SWIPE_GUARD_CLASS)) return true;
        const style = getComputedStyle(el);
        if (
            el.scrollHeight > el.clientHeight &&
            (style.overflowY === "auto" || style.overflowY === "scroll")
        ) return true;
        el = el.parentElement;
    }
    return false;
}

export function useSwipe(
    onSwipeLeft: () => void,
    onSwipeRight: () => void,
    onSwipeUp?: () => void,
    onSwipeDown?: () => void,
) {
    const startX = useRef<number | null>(null);
    const startY = useRef<number | null>(null);
    const cancelled = useRef(false);

    return {
        onTouchStart: (e: React.TouchEvent) => {
            startX.current = e.touches[0].clientX;
            startY.current = e.touches[0].clientY;
            // Pre-cancel if touch starts inside a swipe-guard element (e.g. Monaco editor).
            // Monaco stops propagation of touchmove internally, so the guard check in
            // onTouchMove would never run — without this, touchend still fires and the
            // accumulated displacement incorrectly triggers section navigation.
            cancelled.current = hasSwipeGuard(e.target as Element);
        },
        onTouchMove: (e: React.TouchEvent) => {
            if (cancelled.current || startX.current === null || startY.current === null) return;
            const dx = Math.abs(e.touches[0].clientX - startX.current);
            const dy = Math.abs(e.touches[0].clientY - startY.current);
            if (dy > dx) {
                if (!onSwipeUp && !onSwipeDown) cancelled.current = true;
                else if (hasVerticalScroll(e.target as Element)) cancelled.current = true;
            } else if (dx > dy && hasHorizontalScroll(e.target as Element)) {
                cancelled.current = true;
            }
        },
        onTouchEnd: (e: React.TouchEvent) => {
            if (startX.current === null || startY.current === null || cancelled.current || hasSwipeGuard(e.target as Element)) {
                startX.current = null;
                startY.current = null;
                cancelled.current = false;
                return;
            }
            const dx = e.changedTouches[0].clientX - startX.current;
            const dy = e.changedTouches[0].clientY - startY.current;
            if (Math.abs(dy) > Math.abs(dx)) {
                if (dy < -SWIPE_THRESHOLD) onSwipeUp?.();
                else if (dy > SWIPE_THRESHOLD) onSwipeDown?.();
            } else {
                if (dx < -SWIPE_THRESHOLD) onSwipeLeft();
                else if (dx > SWIPE_THRESHOLD) onSwipeRight();
            }
            startX.current = null;
            startY.current = null;
            cancelled.current = false;
        },
    };
}
